import {
  BookOpenTextIcon,
  Button,
  Card,
  Modal,
  TextLink,
  Typography,
} from "@codat/orchard-ui";
import { Product } from "api/schema/productsSchema";
import React, { useEffect, useState } from "react";
import { productIds } from "utils";

import ContactSales from "components/ContactSales";

import { ProductSelectCard } from "../ProductCards";
import styles from "./ProductSelect.module.scss";

// do not include spend insights as it is Codat managed rather than user
const userManagedProductIds: string[] = [
  productIds.lending,
  productIds.bankFeeds,
  productIds.syncForCommerce,
  productIds.syncForExpensesV1,
  productIds.syncForExpensesV2,
  productIds.payablesV1,
  productIds.payablesV2,
  productIds.syncForPayroll,
];

export interface ProductSelectProps {
  products: Product[];
  onProductSelect: (product: Product) => void;
}

export const ProductSelect: React.FC<ProductSelectProps> = ({
  onProductSelect,
  products,
}: ProductSelectProps) => {
  const [selectedProduct, setSelectedProduct] = useState<Product | undefined>();
  const [contactOpen, setContactOpen] = useState(false);

  const [userManagedProducts, setUserManagedProducts] = useState<Product[]>([]);

  useEffect(() => {
    const filteredProducts = userManagedProductIds.reduce<Product[]>(
      (acc, id) => {
        const product = products.find((p) => p.productId === id);
        return product ? [...acc, product] : acc;
      },
      []
    );
    setUserManagedProducts(filteredProducts);
  }, [products]);

  return (
    <>
      <Card className={styles.wrapper} data-testid="product-select">
        <div className={styles.cardHeader}>
          <Typography
            as="h1"
            variant="heading_xl"
            className={styles.cardHeading}
          >
            <span>Which product</span>&nbsp;
            <span>do you want to get started with?</span>
          </Typography>
          <Button
            size="small"
            color="interface"
            variant="secondary"
            href="https://docs.codat.io/configure/products"
            target="_blank"
            rel="noreferrer"
          >
            <span className={styles.learnButtonContent}>
              <BookOpenTextIcon />
              <span>Learn about our products</span>
            </span>
          </Button>
        </div>
        <div className={styles.productGrid}>
          {userManagedProducts.map((product) => {
            const selected = product.productId === selectedProduct?.productId;
            return (
              <ProductSelectCard
                key={product.productId}
                product={product}
                onCardClick={() => {
                  setSelectedProduct(selected ? undefined : product);
                }}
                selected={selected}
              />
            );
          })}
        </div>
        <div className={styles.cardFooter}>
          <Typography as="p" variant="body_sm">
            Not sure what to choose?{" "}
            <TextLink onClick={() => setContactOpen(true)} tabIndex={0}>
              Talk to us
            </TextLink>
          </Typography>
          <Button
            data-testid="continue-button"
            disabled={!selectedProduct}
            onClick={() => {
              selectedProduct && onProductSelect(selectedProduct);
            }}
          >
            {selectedProduct
              ? `Use ${selectedProduct.name}`
              : "Select a product to continue"}
          </Button>
        </div>
      </Card>
      {contactOpen && (
        <Modal onClose={() => setContactOpen(false)} size="small">
          <ContactSales onClose={() => setContactOpen(false)} />
        </Modal>
      )}
    </>
  );
};
